// Trope-template generator for NA pitch titles.
// PRINCIPLE: every template below is a valid NA-style fantasy trope.
// We fill only the curated variable slots. No random cross-pool mashing.

type SettingId =
    'bedroom' |
    'office' |
    'pool' |
    'hotel' |
    'gym' |
    'kitchen' |
    'living' |
    'classroom' |
    'massage';

// --- Curated slot pools ---

const FLAVORS = ['Hot', 'Sexy', 'Naughty', 'Busty', 'Curvy', 'Lonely', 'Bored'];
const FLAVORS_SOFT = ['Hot', 'Sexy', 'New'];

const FRIEND_RELATIONS = [
    'Friend',
    'Sister',
    'Brother',
    'Roommate',
    'Best Friend'];

const SPOUSE_RELATIONS = ['Wife', 'Husband', 'Girlfriend', 'Boyfriend'];
const FAMILY_RELATIONS = ['Daughter', 'Son', 'Stepdaughter', 'Stepson'];

const MOM_ARCHETYPES = ['Mom', 'Stepmom', 'Aunt', 'Older Sister'];
const FRIEND_ARCHETYPES = [
    'Hot Friend',
    'Best Friend',
    'Sexy Friend',
    'Busty Friend'];

const WORKPLACE_ROLES = [
    'Boss',
    'Assistant',
    'Intern',
    'Coworker',
    'New Hire',
    'Client'];

const NEIGHBOR_ARCHETYPES = [
    'Neighbor',
    'Housewife',
    'Babysitter',
    'Cleaning Lady',
    'Landlord\u2019s Wife'];

const AUTHORITY_ROLES = [
    'Teacher',
    'Professor',
    'Tutor',
    'Substitute',
    'Coach',
    'Trainer'];


// --- Template definitions ---
// `opts.settings` is the user's chosen settings array (may be empty).
// Setting-aware templates fire if ANY selected setting matches their gate.

type Template = (opts: {settings: string[];}) => string | null;

function pick<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
}

function has(settings: string[], id: SettingId): boolean {
    return settings.includes(id);
}

const TEMPLATES: Template[] = [
// "My Friend's Hot Mom" — the canonical NA trope
    () =>
        `My ${pick(FRIEND_RELATIONS)}\u2019s ${pick(FLAVORS)} ${pick(MOM_ARCHETYPES)}`,

// "My Daughter's Hot Friend" — mirror trope
    () => `My ${pick(FAMILY_RELATIONS)}\u2019s ${pick(FRIEND_ARCHETYPES)}`,

// "My Wife's Hot Friend" / "My Husband's Sexy Boss"
    () => `My ${pick(SPOUSE_RELATIONS)}\u2019s ${pick(FRIEND_ARCHETYPES)}`,
    () =>
        `My ${pick(SPOUSE_RELATIONS)}\u2019s ${pick(FLAVORS)} ${pick(WORKPLACE_ROLES)}`,

// "Hot Neighbor Next Door" / "Sexy Housewife Next Door"
    () => `${pick(FLAVORS)} ${pick(NEIGHBOR_ARCHETYPES)} Next Door`,

// "The Lonely Housewife"
    () =>
        `The ${pick(FLAVORS)} ${pick([...NEIGHBOR_ARCHETYPES, ...MOM_ARCHETYPES])}`,

// "The Neighbor Affair" — short + evocative
    () => `The ${pick(NEIGHBOR_ARCHETYPES)} Affair`,
    () => `${pick(FLAVORS_SOFT)} ${pick(MOM_ARCHETYPES)} on Vacation`,

// Setting-aware templates
    ({ settings }) =>
        has(settings, 'office') ?
            `My ${pick(FLAVORS)} ${pick(WORKPLACE_ROLES)}` :
            null,
    ({ settings }) =>
        has(settings, 'office') ? `The ${pick(WORKPLACE_ROLES)} After Hours` : null,
    ({ settings }) =>
        has(settings, 'classroom') ?
            `My ${pick(FLAVORS_SOFT)} ${pick(AUTHORITY_ROLES)}` :
            null,
    ({ settings }) =>
        has(settings, 'classroom') ?
            `The ${pick(FLAVORS)} ${pick(AUTHORITY_ROLES)}` :
            null,
    ({ settings }) =>
        has(settings, 'gym') ?
            `My ${pick(FLAVORS_SOFT)} ${pick(['Trainer', 'Yoga Instructor', 'Gym Partner'])}` :
            null,
    ({ settings }) =>
        has(settings, 'pool') ?
            `The ${pick(FLAVORS)} ${pick(['Neighbor', 'Pool Cleaner', 'Lifeguard'])}` :
            null,
    ({ settings }) =>
        has(settings, 'hotel') ? `${pick(FLAVORS)} Stranger at the Hotel` : null,
    ({ settings }) =>
        has(settings, 'hotel') ?
            `The ${pick(['Bride', 'Businesswoman', 'Honeymooner'])} Next Door` :
            null,
    ({ settings }) =>
        has(settings, 'massage') ? `My ${pick(FLAVORS_SOFT)} Masseuse` : null,
    ({ settings }) =>
        has(settings, 'massage') ? `The ${pick(FLAVORS)} Massage` : null,
    ({ settings }) =>
        has(settings, 'kitchen') ? `The ${pick(FLAVORS)} Housewife` : null,
    ({ settings }) =>
        has(settings, 'bedroom') || has(settings, 'living') ?
            `My ${pick(FLAVORS_SOFT)} Roommate` :
            null];


function generateOneFromIndex(
    index: number,
    settings: string[])
    : string | null {
    const t = TEMPLATES[index];
    if (!t) return null;
    const out = t({ settings });
    if (!out) return null;
    return out.replace(/\s+/g, ' ').trim();
}

// Shuffle-an-array helper — Fisher-Yates
function shuffleIndices(n: number): number[] {
    const arr = Array.from({ length: n }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

/**
 * Generate N unique titles, biased to use DIFFERENT templates per batch
 * so the user sees variety (not three variants of the same trope).
 * Falls back gracefully if template pool is exhausted.
 */
export function generateUniqueTitles(
    count: number,
    settings: string[] = [])
    : string[] {
    const seen = new Set<string>();
    const result: string[] = [];

    // First pass: one title per template, shuffled
    const order = shuffleIndices(TEMPLATES.length);
    for (const idx of order) {
        if (result.length >= count) break;
        const t = generateOneFromIndex(idx, settings);
        if (t && !seen.has(t)) {
            seen.add(t);
            result.push(t);
        }
    }

    // Second pass: any template, fill remaining slots
    let attempts = 0;
    while (result.length < count && attempts < 80) {
        const idx = Math.floor(Math.random() * TEMPLATES.length);
        const t = generateOneFromIndex(idx, settings);
        if (t && !seen.has(t)) {
            seen.add(t);
            result.push(t);
        }
        attempts++;
    }

    // Safety fallback
    while (result.length < count) result.push('My Friend\u2019s Hot Mom');
    return result;
}

// Back-compat export
export function generateThreeUniqueTitles(settings: string[] = []): string[] {
    return generateUniqueTitles(3, settings);
}
